import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SubHeaderMobileOption } from "../../Components/SubHeaderMobileOption/SubHeaderMobileOption.jsx";
import DataTableSubHeader from "../../Components/DataTableSubHeader/DataTableSubHeader.jsx";
import ItemsTable from "../../Components/ItemsTable/ItemsTable.jsx";
import { toast } from "react-toastify";
import { getUsers, deleteUser, getOrganizations } from "../../api/index.js";
import Spinner from "../../Components/ProgressBar/Spinner.js";
import {
  AddIcon,
  FilterIcon,
} from "../../assets/icons/index.js";
import useTransformNewData from "../../hooks/useTransformNewData.js";
import AddUserModal from "../../Components/Users/AddUserModal/AddUserModal.jsx";
import DeleteModal from "../../Components/Common/DeleteModal/DeleteModal.jsx";
import FilterModal from "../../Components/FilterModal/index.jsx";
import ReactModal from "../../Components/ReactModal/ReactModal.jsx";
import { useAppContext } from "../../Components/Context/AppContext.jsx";
import "./UsersDetailsTable.scss";
import userHasPermission from "../../helpers/formTools/userHasPermission/index.js";
import userOrganizationsHelper from "../../helpers/formTools/userOrganizations/index.js";
import AdminDropdown from "../../Components/AdminDropdown/AdminDropdown.jsx";

const breadcrumbsData = [
  {
    id: 1,
    label: `Amministrazione ${localStorage.getItem("username")}`,
    iconUrl: true,
  },
  {
    id: 2,
    label: "UTENTI",
    iconUrl: "",
  },
];

const splitUsersByOrganization = (users) => {
  return users.reduce((acc1, user) => {
    const userOrganizations = userOrganizationsHelper(user);

    userOrganizations.forEach(organization => {
      acc1.push({
        organization: { name: organization.name, id: organization.id },
        roles: user.roles.filter(role => role.organization?.id === organization.id || (role.organization === null && organization.id === null)),
        groups: user.groups.filter(group => group.organization?.id === organization.id || (group.organization === null && organization.id === null)),
        username: user.username,
        email: user.email,
      });
    });

    if(userOrganizations.length === 0) {
      acc1.push({
        username: user.username,
        email: user.email,
        organization: { name: 'NA', id: null },
        roles: [],
        groups: [],
      });
    }
    return acc1;
  }, []);
};

const filterValues = [
  { label: "Username", key: "username" },
];

const UsersDetailsTable = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuActive, setIsMenuActive] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [splittedUserData, setSplittedUserData] = useState([]);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [sortDirection, setSortDirection] = useState("asc");

  const { userFilter, setUserFilter, userProfile, setSelectedAdminItem } = useAppContext();
  const organization = JSON.parse(localStorage.getItem("organization"));
  const userCanRead = userHasPermission({
    userProfile,
    organization,
    resource: "USER",
    permission: "WRITE",
  });
  const userCanWrite = userHasPermission({
    userProfile,
    organization,
    resource: "USER",
    permission: "WRITE",
  });
  const userIsSuperAdmin = userProfile?.roles?.some(role => role.authorities?.includes('ADMIN') && role.organization === null);

  const selectedHeaders = [
    { key: "actions", label: "", width: "100px", sortable: false },
    { key: "userName", label: "Nome utente", width: "300px", sortable: false },
    userIsSuperAdmin ? { key: "organization", label: "Organizzazione", width: "300px", sortable: false } : {},
    { key: "roles", label: "Ruoli", width: "500px", sortable: false },
    { key: "groups", label: "Gruppi", width: "500px", sortable: false },
  ];

  const handleViewUser = useCallback((user) => {
    setSelectedAdminItem(user);
    navigate("/users/view");
  }, [navigate, setSelectedAdminItem]);

  const handleDeleteClick = useCallback((user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  }, []);

  const transformNewData = useTransformNewData(
    splittedUserData,
    handleViewUser,
    handleDeleteClick,
    userCanWrite
  );

  const tableData = useMemo(() => {
    return transformNewData;
  }, [transformNewData]);

  const fetchUsers = useCallback(async () => {
    if (!userCanRead) {
      setIsLoading(false);
      return;
    }

    const initialFilter = [
      { key: "page", value: currentPage },
      { key: "pageSize", value: recordsPerPage },
    ];
    const filter = [...initialFilter, ...userFilter];

    setIsLoading(true);
    try {
      const response = await getUsers(filter);
      const users = response?.results ?? response?.users ?? [];

      if (Array.isArray(users)) {
        setSplittedUserData(splitUsersByOrganization(users));
        setTotalRecords(response?.totalElements ?? users.length);
      } else {
        toast.error(`Unknown error listing users`);
      }
    } catch (err) {
      toast.error("Error while fetching users");
    } finally {
      setIsLoading(false);
    }
  }, [userFilter, currentPage, recordsPerPage, userCanRead]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const organizations = await getOrganizations({ pageSize: 100, page: 1 });
      if (organizations) {
        setOrganizationsData(organizations.map((org) => ({
          value: org.id,
          label: org.name,
        })));
      }
    } catch (err) {
      console.error("Error while fetching organizations:", err);
    }
  }, []);

  const handleAddModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleUserAdd = useCallback(async () => {
    handleAddModalClose();
    await fetchUsers();
  }, [handleAddModalClose, fetchUsers]);

  const handleCallBackFilter = useCallback(
    (data) => {
      const filter = data.map((item) => ({
        key: filterValues?.find((f) => f.label === item.label)?.key,
        value: item.value,
      }));
      setUserFilter(filter);
      setCurrentPage(1);
      return true;
    },
    [setUserFilter]
  );

  const handleCallBackSort = useCallback(
    (key) => {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      setUserFilter([{ key: "sort", value: `${key},${sortDirection}` }]);
    },
    [setUserFilter, sortDirection]
  );

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRecordsPerPageChange = useCallback((newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  }, []);

  const handleUserDelete = useCallback(
    async (username) => {
      try {
        await deleteUser(username);
        toast.success("User deleted successfully");
      } catch (err) {
        console.error("error--->>>", err);
        toast.error("Error while deleting user");
      } finally {
        handleDeleteModalClose();
        await fetchUsers();
      }
    },
    [fetchUsers, handleDeleteModalClose]
  );

  const subHeaderRightNavOptions = {
    icons: [
      userCanWrite && {
        icon: <AddIcon fillColor="var(--archeo-color)" />,
        onClick: () => setIsModalOpen(true),
        label: "Aggiungi",
      },
      {
        icon: <FilterIcon fillColor="var(--archeo-color)" height="28" width="28" />,
        onClick: () => setIsFilterModalOpen(true),
        label: "Filtro",
      },
    ].filter(Boolean),
    components: [
      <AdminDropdown defaultSelected="UTENTI"/>,
    ],
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (organization.id === null) {
      fetchOrganizations();
    }
  }, [organization.id, fetchOrganizations]);

  return (
    <div className="container">
      <main className="page-main-content">
        <DataTableSubHeader
          title={"UTENTI"}
          breadcrumbsData={breadcrumbsData}
          subHeaderRightNavOptions={subHeaderRightNavOptions}
          isMenuActive={isMenuActive}
          setMenuActive={() => setIsMenuActive(!isMenuActive)}
        />
       {isLoading ? (
            <Spinner />
          ) : (
          <ItemsTable
            data={tableData}
            headers={selectedHeaders}
            filterValues={filterValues}
            totalRecords={totalRecords}
            currentPage={currentPage}
            recordsPerPage={recordsPerPage}
            onPageChange={handlePageChange}
            onRecordsPerPageChange={handleRecordsPerPageChange}
            callBackSort={handleCallBackSort}
            userCanRead={userCanRead}
          />
          )}
      </main>
      {isMenuActive && (
        <SubHeaderMobileOption
          components={subHeaderRightNavOptions?.components}
          icons={subHeaderRightNavOptions?.icons}
          onCrossClick={() => setIsMenuActive(false)}
          title="options"
        />
      )}
      {isModalOpen && (
        <AddUserModal
          organization={organization}
          organizationsData={organizationsData}
          isOpen={isModalOpen}
          onClose={handleAddModalClose}
          onSave={handleUserAdd}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteModal
          show={isDeleteModalOpen}
          item={selectedUser?.username}
          handleClose={handleDeleteModalClose}
          handleDelete={handleUserDelete}
          itemType="User"
        />
      )}
      <ReactModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
      >
        <FilterModal
          closeModal={() => setIsFilterModalOpen(false)}
          filterValues={filterValues}
          callBackFilter={handleCallBackFilter}
          onReset={() => {
            setUserFilter([]);
            setCurrentPage(1);
            window.location.reload();
          }}
        />
      </ReactModal>
    </div>
  );
};

export default UsersDetailsTable;
